import { useEffect, useState } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { atualizarCampoConfig, ouvirClinica } from '@/services/clinicasService';
import {
  adicionarOrigem,
  definirPalavras,
  inativarOrigem,
  listaDeOrigens,
  moverOrigem,
  reativarOrigem,
  renomearOrigem,
} from '@/leads/origens';

/**
 * Origens do lead (VH) — a lista de onde os leads vêm, editável pelo gestor.
 *
 * ★ Adicionar, REORDENAR (a ordem é a do select e a prioridade da palavra solta),
 * renomear, remover e as PALAVRAS de cada origem (o que, na primeira mensagem,
 * denuncia a origem — só vale com a inferência ligada em Captação). "Remover"
 * NUNCA apaga: marca `ativo: false`, some dos selects e continua legível em quem
 * já tem. Um id nunca é apagado nem reaproveitado.
 *
 * ★ A aba semeia da MESMA fonte que a ficha e o servidor leem
 * (`config.origens ?? PADRÃO`): abrir e salvar sem tocar grava exatamente a
 * lista que o produto já usava. Componente PRÓPRIO — `SecaoMotivos` não é tocado.
 */
export default function SecaoOrigens() {
  const { clinicaId } = useAuth();
  const [clinica, setClinica] = useState(null);
  const [lista, setLista] = useState(null);
  const [nova, setNova] = useState('');
  const [novasPalavras, setNovasPalavras] = useState('');
  const [editando, setEditando] = useState(null); // id
  const [labelEdit, setLabelEdit] = useState('');
  const [erro, setErro] = useState(null);
  const [salvo, setSalvo] = useState(false);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (!clinicaId) return undefined;
    return ouvirClinica(clinicaId, setClinica, () => setErro('Não foi possível carregar a configuração.'));
  }, [clinicaId]);

  const config = clinica?.config ?? null;
  useEffect(() => {
    setLista(listaDeOrigens(config));
    setSalvo(false);
  }, [config]);

  const mudar = (novaLista) => { setLista(novaLista); setSalvo(false); };

  const adicionar = () => {
    if (!nova.trim()) return;
    mudar(adicionarOrigem(lista, nova, novasPalavras));
    setNova('');
    setNovasPalavras('');
  };

  async function salvar() {
    setErro(null);
    setOcupado(true);
    try {
      await atualizarCampoConfig(clinicaId, 'origens', lista);
      setSalvo(true);
    } catch (e) {
      setErro(e?.code === 'permission-denied' ? 'Só o gestor altera as origens.' : (e.message ?? 'Não foi possível salvar.'));
    } finally {
      setOcupado(false);
    }
  }

  if (!lista) return <p className="vazio">Carregando as origens…</p>;

  return (
    <section className="secao" data-secao="origens">
      <h2 className="secao-titulo">Origens do lead</h2>
      <p className="secao-sub">
        De onde os leads vêm. A ordem é a do select — deixe as mais comuns em cima. As
        <strong> palavras</strong> são o que, na primeira mensagem, denuncia a origem (vale só com
        “Inferir a origem pela primeira mensagem” ligado em Captação); Fachada e Paciente antigo
        não têm sinal na mensagem — ficam para a SDR. Remover não apaga: a origem some dos selects
        e continua legível em quem já a tem.
      </p>

      <ul className="lista-motivos lista-origens">
        {lista.map((o, i) => (
          <li key={o.id} className={o.ativo ? undefined : 'motivo-inativo'}>
            {editando === o.id ? (
              <>
                <input
                  aria-label={`Novo nome para ${o.label}`}
                  value={labelEdit}
                  disabled={ocupado}
                  onChange={(e) => setLabelEdit(e.target.value)}
                />
                <button type="button" className="btn-texto" disabled={ocupado}
                  onClick={() => { mudar(renomearOrigem(lista, o.id, labelEdit)); setEditando(null); }}>
                  salvar nome
                </button>
                <button type="button" className="btn-texto" onClick={() => setEditando(null)}>cancelar</button>
              </>
            ) : (
              <>
                <span className="motivo-rotulo">{o.label}</span>
                <input
                  className="origem-palavras"
                  aria-label={`Palavras de ${o.label}`}
                  placeholder="palavras, separadas por vírgula"
                  value={o.palavras.join(', ')}
                  disabled={ocupado || !o.ativo}
                  onChange={(e) => mudar(definirPalavras(lista, o.id, e.target.value))}
                />
                <button type="button" className="btn-texto" disabled={ocupado || i === 0}
                  title="Subir" onClick={() => mudar(moverOrigem(lista, o.id, -1))}>↑</button>
                <button type="button" className="btn-texto" disabled={ocupado || i === lista.length - 1}
                  title="Descer" onClick={() => mudar(moverOrigem(lista, o.id, 1))}>↓</button>
                <button type="button" className="btn-texto" disabled={ocupado}
                  onClick={() => { setEditando(o.id); setLabelEdit(o.label); }}>renomear</button>
                {o.ativo ? (
                  <button type="button" className="btn-texto perigo" disabled={ocupado}
                    title="Some dos selects; quem já tem esta origem continua com ela"
                    onClick={() => mudar(inativarOrigem(lista, o.id))}>remover</button>
                ) : (
                  <button type="button" className="btn-texto" disabled={ocupado}
                    onClick={() => mudar(reativarOrigem(lista, o.id))}>reativar</button>
                )}
              </>
            )}
          </li>
        ))}
      </ul>

      <div className="linha">
        <div className="campo">
          <label htmlFor="nova-origem">Nova origem</label>
          <input id="nova-origem" value={nova} disabled={ocupado} placeholder="ex.: Feira de saúde"
            onChange={(e) => setNova(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); adicionar(); } }} />
        </div>
        <div className="campo">
          <label htmlFor="nova-origem-palavras">Palavras (opcional)</label>
          <input id="nova-origem-palavras" value={novasPalavras} disabled={ocupado} placeholder="feira, stand"
            onChange={(e) => setNovasPalavras(e.target.value)} />
        </div>
        <button type="button" className="btn-secundario" disabled={ocupado || !nova.trim()} onClick={adicionar}>
          Adicionar origem
        </button>
      </div>

      {erro && <p className="erro">{erro}</p>}
      <footer className="dialogo-acoes">
        {salvo && <span className="campo-ajuda">Salvo.</span>}
        <button type="button" className="btn-primario" disabled={ocupado} onClick={salvar}>
          {ocupado ? 'Salvando…' : 'Salvar origens'}
        </button>
      </footer>
    </section>
  );
}
